/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-let */

const {
  Default: _, group, match, isSome,
} = require('@nike/rcf-fp');

const { TRUE_APP, NPOS_APP } = require('./constants');
const { isEMEA } = require('./country-mappings');
const { countryToGeoMap } = require('./static/nike-geo-mappings');
const storeRestrictionsInNpos = require('./static/store-restriction.json');
const mockStoreRestrictionsInNpos = require('./static/store-restriction.mock.json');
const { isCypress } = require('./testUtils');

const emailRegex = /\S+@\S+\.\S+/;

/* Helper Functions */
const sortItems = (lineItems) => group(
  lineItems,
  ((item) => match(item)(
    [() => isSome(item.Sale), 'Sales'],
    [() => isSome(item.Return), 'Returns'],
    [() => isSome(item.Tax), 'Tax'],
    [() => isSome(item.Tender || item.TenderChange), 'Payments'],
    [() => isSome(item.SaleForDelivery), 'Delivery'],
    [() => isSome(item.SaleForPickup), 'Pickup'],
    [_, 'Other'],
  )), // key extractor
);

/* Removes Mid number from China */
const removeMidNumber = (jsonPosLog) => {
  const lineItems = (jsonPosLog.Transaction.RetailTransaction?.LineItems || jsonPosLog.Transaction.OrderTransaction?.LineItems || []);
  lineItems.forEach((obj, index) => {
    if (obj?.Tender?.Authorizations?.[0]?.Mid) {
      // eslint-disable-next-line fp/no-mutation
      lineItems[index].Tender.Authorizations[0].Mid = lineItems[index].Tender.Authorizations[0].Mid.replace(/.(?=.{4})/g, 'X');
    }
  });
  return jsonPosLog;
};

const translateByLocale = (
  getTranslation, keys, localeInfo, localeName
) => keys.reduce(
  (acc, key) => (
    {
      ...acc,
      [key]: getTranslation(key, localeName) || getTranslation(key, 'en-US')
    }
  ),
  {
    locale: localeInfo.momentFormat
      ? localeInfo
      : { ...localeInfo, momentFormat: 'MM/DD/YYYY' }
  }
);
const translateBodyByLocale = (
  getTranslation, keys, localeName
) => keys.reduce(
  (acc, key) => (
    {
      ...acc,
      [key]: getTranslation(key, localeName || 'en-US') || getTranslation(key, 'en-US')
    }
  ),
  {
    translatedLocaleName: localeName
  }
);

const isValidHttpUrl = (string) => {
  let url;
  try {
    url = new URL(string);
  } catch (ex) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

const openLink = (url) => {
  if (!isValidHttpUrl(url)) return;

  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

const isNegativeAmount = (num) => Math.sign(num) === -1;

/* Errors */
const isHiveAthenaError = ({ message, statusCode }) => message.includes('HIVE_BAD_DATA') && (statusCode === 500);

const isGreaterChinaGeo = (countryCode) => countryToGeoMap[countryCode] === 'GREATER_CHINA';

const shouldShowCardNumberFilter = (paymentType, storeLocale) => (storeLocale === 'en-IN' ? ['GiftCard'].includes(paymentType) : ['GiftCard', 'Debit', 'Credit', 'CreditDebit'].includes(paymentType));

const getTenderTypeColumnWidth = (fields, isGreaterChinaOnlinePayment, storeLocale) => ((shouldShowCardNumberFilter(fields, storeLocale) || isGreaterChinaOnlinePayment) ? '6' : '12');

const isProdEnv = () => process.env.NODE_ENV === 'production';

const getStyles = (isNpos, styleName) => {
  switch (styleName) {
    case 'filters':
      return {
        backgroundColor: '#FFFFFF',
        ...(isNpos
          ? { boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)', height: '24vh' }
          : { boxShadow: 'none' })
      };
    case 'results':
      return {
        backgroundColor: isNpos ? '#F5F5F5' : '#FFFFFF',
        ...(isNpos
          ? { backgroundColor: '#F5F5F5', height: '75vh' }
          : { backgroundColor: '#FFFFFF', paddingBottom: '8px' })
      };
    case 'receipt-view':
      return isNpos
      ? {
          maxHeight: '60vh', overflowY: 'scroll', position: 'sticky', top: 0
      }
      : { paddingTop: '16px' };
    default:
      return {};
  }
};

const createArray = (len) => Array.from({ length: len }, (_a, index) => index + 1);

// Source- https://stackoverflow.com/a/9204568
const isValidEmail = (email) => (!email || emailRegex.test(String(email).toLowerCase()));

const parseNposResponse = (response) => (typeof response === 'string' ? JSON.parse(response.replaceAll("'", '"')) : response);

/* Stores which should not be listed in Store Selection in NPOS */
const getStoresToHideFromStoresList = (loggedInStore) => {
  const stores = isCypress() ? mockStoreRestrictionsInNpos : storeRestrictionsInNpos;

  return stores.filter((restrictedStore) => restrictedStore.storeNumber !== loggedInStore?.full?.storeNumber
      && restrictedStore.country === loggedInStore?.full?.address?.country
      && restrictedStore.hideFromOtherStores).map((store) => store.storeNumber);
};

/* Stores for which Store Switching should be disabled in NPOS */
const getStoresWithStoreSwitchDisabled = () => {
  if (isCypress()) {
    /* window.CYPRESS_OVERRIDE_STORE_SWITCH_DISABLE flag is used to handle conflicting test requirements */
    return window.CYPRESS_OVERRIDE_STORE_SWITCH_DISABLE ? [] : mockStoreRestrictionsInNpos;
  }

  return storeRestrictionsInNpos;
};

const isStoreSwitchingRestrictedInNpos = (storeNumber) => getStoresWithStoreSwitchDisabled()?.find((store) => store.storeNumber === storeNumber)?.disableStoreSwitching === true;

const shouldDisableStoreSelection = (appSource) => match(appSource)(
    [TRUE_APP, true], // For TRUE APP store selection should be always disabled
    [_, false]
  );

/* Filter out restricted stores from store list based on store restrictions */
const filterRestrictedStores = (storesList, loggedInStore, appSource) => {
  if (appSource === TRUE_APP) return []; // store selection is disabled in True Platform

  if (appSource === NPOS_APP) { // hide other restricted Stores from the Stores List in NPOS
      const hideOtherStores = isStoreSwitchingRestrictedInNpos(loggedInStore?.full?.storeNumber);

      if (hideOtherStores) return storesList.filter((store) => store.full.storeNumber === loggedInStore?.full?.storeNumber);

      const restrictedStoreNumbers = getStoresToHideFromStoresList(loggedInStore);
      return storesList.filter((store) => !restrictedStoreNumbers.includes(store.full.storeNumber));
  }
  return storesList;
};

const getLocaleOverride = (localeName) => (localeName === 'nn-NO' ? 'nb' : localeName);

// EMEA now supports 24 months of history, but the Bodega translations for
// RL_ErrorMsgForOlderDataEJ still reference 13 months. Until the translations
// are updated, swap "13" -> "24" in the localized string when possible;
// otherwise fall back to this English message (e.g. Arabic uses "١٣").
const OLDER_DATA_EMEA_FALLBACK = 'Receipts older than 24 months can’t be fetched here. Please raise an incident ticket as per the EJ KB article.';

const getHistoricalDataErrorMessage = (localizedMessage, countryCode) => {
  if (!isEMEA(countryCode)) return localizedMessage;
  if (localizedMessage && localizedMessage.includes('13')) return localizedMessage.replace('13', '24');
  return OLDER_DATA_EMEA_FALLBACK;
};

module.exports = {
  createArray, filterRestrictedStores, getHistoricalDataErrorMessage, getLocaleOverride, getStyles, getTenderTypeColumnWidth, isGreaterChinaGeo, isHiveAthenaError, isNegativeAmount, isProdEnv, isValidEmail, isValidHttpUrl, openLink, parseNposResponse, removeMidNumber, shouldDisableStoreSelection, shouldShowCardNumberFilter, sortItems, translateBodyByLocale, translateByLocale
};
